import React from 'react';

import { IPersonaFront } from '@api/routers/user/service';
import styled from '@emotion/styled';
import { useTranslations } from 'next-intl';
import { formatEther } from 'viem';

import { Button } from '@web/ui/components';
import { IModalProps } from '@web/ui/components/modal';

import { useClaimAll, usePersonaRewards } from '../hooks';

import { BaseStakingModal } from './BaseStakingModal';
import { StakingAlert } from './components/StakingAlert';
import { StakingStep } from './components/StakingStep';
import { getModalStatus } from './getModalStatus';

const Action = styled(Button)`
  white-space: nowrap;
`;

export interface IClaimModalProps extends IModalProps {
  personas: IPersonaFront[];
}

export const ClaimModal = ({
  personas,
  onClose,
  open,
  ...props
}: IClaimModalProps) => {
  const t = useTranslations();

  const rewards = usePersonaRewards(
    personas.map((p) => p.tokenId),
    { enabled: open },
  );

  const claimAll = useClaimAll();

  const status = getModalStatus([
    claimAll.mutation.status,
    claimAll.receipt.status,
  ]);

  const handleClose = () => {
    claimAll.mutation.reset();
    onClose();
  };

  return (
    <BaseStakingModal
      personas={personas}
      status={status}
      title={t('CLAIM_MODAL_TITLE', {
        rewards: Number(formatEther(rewards)).toFixed(2),
      })}
      completedText={t('CLAIM_MODAL_COMPLETED_TEXT')}
      onClose={handleClose}
      actions={[
        <Action
          label={t('CLAIM_MODAL_ACTION_CONFIRM')}
          onClick={() =>
            claimAll.mutation.mutate({
              tokenIds: personas.map((p) => p.tokenId),
            })
          }
          fullWidth
        />,
        <Action
          label={t('CLAIM_MODAL_ACTION_CANCEL')}
          onClick={handleClose}
          variant="secondary"
          color="onSurface"
          background="surface"
          fullWidth
        />,
      ]}
      open={open}
      {...props}
    >
      {status === 'error' && (
        <StakingAlert>{t('STAKING_ALERT_ERROR')}</StakingAlert>
      )}
      <StakingStep
        title={t('STAKING_STEP_CLAIM_TITLE')}
        description={t('STAKING_STEP_CLAIM_DESCRIPTION')}
        status={status}
        type="claim"
      />
    </BaseStakingModal>
  );
};
